import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, TrendingUp, Bell, Calendar, ArrowRight, Building2, Landmark, Home, ChevronRight, Flame, Clock, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PropertyCard from '../components/PropertyCard';
import { api } from '../api';
import Marquee from 'react-fast-marquee';

const HomePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [todaysDrops, setTodaysDrops] = useState([]);
  const [endingSoon, setEndingSoon] = useState([]);
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [auctionHouses, setAuctionHouses] = useState([]);
  const [stats, setStats] = useState({ total: 0, houses: 47 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [propertiesRes, housesRes] = await Promise.all([
        api.getProperties({ limit: 50 }),
        api.getAuctionHouses()
      ]);
      
      const allProperties = propertiesRes.data || [];
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);

      // Filter today's drops (created today or newest)
      const todayProps = allProperties
        .filter(p => {
          const created = new Date(p.created_at || p.auction_date);
          return created >= today;
        })
        .slice(0, 6);
      
      // If no properties created today, show newest
      const drops = todayProps.length > 0 ? todayProps : allProperties.slice(0, 6);
      
      // Filter ending soon (auction within 72 hours)
      const ending = allProperties
        .filter(p => {
          const auctionDate = new Date(p.auction_date);
          return auctionDate > now && auctionDate <= threeDaysFromNow;
        })
        .sort((a, b) => new Date(a.auction_date) - new Date(b.auction_date))
        .slice(0, 6);

      setTodaysDrops(drops);
      setEndingSoon(ending);
      setFeaturedProperties(allProperties.slice(0, 6));
      setAuctionHouses(housesRes.data || []);
      setStats({ total: allProperties.length, houses: housesRes.data?.length || 47 });
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/properties?search=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate('/properties');
    }
  };

  const categories = [
    {
      name: 'Residential',
      icon: Home,
      image: 'https://images.unsplash.com/photo-1623241187960-4a57f68560ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxiZWF1dGlmdWwlMjBCcml0aXNoJTIwaG91c2UlMjBVSyUyMGhvbWUlMjBleHRlcmlvcnxlbnwwfHx8fDE3NzM3OTQ1Nzl8MA&ixlib=rb-4.1.0&q=85',
      description: 'Houses, Flats & Apartments',
      count: '2,450+'
    },
    {
      name: 'Commercial',
      icon: Building2,
      image: 'https://images.unsplash.com/photo-1651331189285-64ef5906d6f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb21tZXJjaWFsJTIwYnVpbGRpbmclMjBvZmZpY2UlMjB0b3dlciUyMGNpdHl8ZW58MHx8fHwxNzczNzk0NTgwfDA&ixlib=rb-4.1.0&q=85',
      description: 'Offices, Retail & Industrial',
      count: '890+'
    },
    {
      name: 'Land',
      icon: Landmark,
      image: 'https://images.unsplash.com/photo-1665881771078-5ef957df8c25?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxlbXB0eSUyMGxhbmQlMjBwbG90JTIwZGV2ZWxvcG1lbnQlMjBzaXRlJTIwZ3JlZW58ZW58MHx8fHwxNzczNzk0NTgxfDA&ixlib=rb-4.1.0&q=85',
      description: 'Development & Agricultural',
      count: '340+'
    }
  ];

  const financeServices = [
    { name: 'Bridging Loans', href: '/finance#bridging' },
    { name: 'Development Finance', href: '/finance#development' },
    { name: 'Commercial Mortgage', href: '/finance#commercial' },
    { name: 'Buy to Let', href: '/finance#buy-to-let' }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      {/* Hero Section - Updated headline */}
      <section 
        className="relative min-h-[80vh] sm:min-h-[85vh] flex items-center justify-center"
        data-testid="hero-section"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/bb2dbecf-0a3e-4591-b943-2c5a6c1854dd/images/ecd01b74e30e52ba976125c84e02af6c28c4ae5ca95d826b47034567755319e6.png)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-800/70" />
        
        <div className="relative z-10 container mx-auto px-4 sm:px-6 md:px-12 py-16 sm:py-20">
          <div className="max-w-4xl mx-auto text-center">
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 bg-[#F5A623]/20 border border-[#F5A623]/30 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 mb-6 sm:mb-8">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              <span className="text-white text-xs sm:text-sm font-medium">{stats.total.toLocaleString()}+ Live Listings</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-4 sm:mb-6 font-heading leading-tight">
              All UK Property Auctions
              <span className="block text-[#F5A623]">In One Place</span>
            </h1>
            
            {/* Sub-headline */}
            <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed px-2">
              40+ auction houses. Updated daily at 6AM.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-8">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search by location, price, property type..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-12 sm:h-14 pl-12 text-sm sm:text-base bg-white text-slate-900 placeholder:text-slate-400 border-0 shadow-lg rounded-xl"
                    data-testid="hero-search-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  size="lg" 
                  className="h-12 sm:h-14 px-6 sm:px-8 bg-[#F5A623] hover:bg-[#E59512] text-white font-semibold rounded-xl shadow-lg shadow-[#F5A623]/25 transition-all hover:shadow-xl hover:shadow-[#F5A623]/30 text-sm sm:text-base"
                  data-testid="hero-search-btn"
                >
                  Search
                </Button>
              </div>
            </form>

            {/* Quick Stats */}
            <div className="flex flex-wrap justify-center gap-6 sm:gap-8 md:gap-12 text-white/80 mb-6">
              <div className="text-center">
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">{stats.total.toLocaleString()}+</div>
                <div className="text-xs sm:text-sm">Live Listings</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">40+</div>
                <div className="text-xs sm:text-sm">Auction Houses</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">6AM</div>
                <div className="text-xs sm:text-sm">Daily Updates</div>
              </div>
            </div>

            {/* Credibility Line */}
            <p className="text-slate-400 text-xs sm:text-sm">
              Used by UK property investors to discover auction deals
            </p>
          </div>
        </div>

        <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden sm:block">
          <ChevronRight className="h-6 w-6 text-white/50 rotate-90" />
        </div>
      </section>

      {/* Auction Houses Ticker */}
      <section className="bg-slate-900 py-4 border-y border-slate-800">
        <Marquee gradient={false} speed={40}>
          {auctionHouses.map((house, idx) => (
            <span key={idx} className="mx-8 text-sm font-medium text-slate-400 hover:text-[#F5A623] transition-colors cursor-default">
              {house.name}
            </span>
          ))}
        </Marquee>
      </section>

      {/* 📊 Live Stats Banner */}
      <section className="bg-white border-b border-slate-100 py-4">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-8">
            <div className="flex items-center gap-2">
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              <span className="text-sm font-semibold text-slate-900">{stats.total.toLocaleString()}+ live listings</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-slate-200"></div>
            <div className="flex items-center gap-2">
              <Flame className="h-4 w-4 text-orange-500" />
              <span className="text-sm font-semibold text-slate-900">{todaysDrops.length} added today</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-slate-200"></div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-red-500" />
              <span className="text-sm font-semibold text-slate-900">{endingSoon.length} ending soon</span>
            </div>
          </div>
        </div>
      </section>

      {/* 🔥 TODAY'S DROPS - New Section */}
      <section className="py-10 sm:py-14 bg-gradient-to-b from-orange-50 to-white" data-testid="todays-drops-section">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 rounded-full px-4 py-2 mb-4">
              <Flame className="h-5 w-5" />
              <span className="font-semibold text-sm">Today's New Listings</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-heading">
              Fresh Properties Just Added
            </h2>
            <p className="text-slate-600 mt-2 text-sm sm:text-base">
              Be the first to see the latest auction opportunities
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3].map(i => (
                <div key={i} className="bg-slate-100 rounded-2xl h-80 animate-pulse" />
              ))}
            </div>
          ) : todaysDrops.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {todaysDrops.map((property) => (
                  <PropertyCard key={property.property_id} property={property} showNewBadge={true} />
                ))}
              </div>
              <div className="text-center mt-8">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/properties?sort=newest')}
                  className="border-orange-300 text-orange-700 hover:bg-orange-50"
                >
                  View All New Listings <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </>
          ) : (
            <p className="text-center text-slate-500">No new listings today. Check back at 6AM!</p>
          )}
        </div>
      </section>

      {/* ⏳ ENDING SOON - New Section */}
      <section className="py-10 sm:py-14 bg-gradient-to-b from-red-50 to-white" data-testid="ending-soon-section">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 rounded-full px-4 py-2 mb-4">
              <Clock className="h-5 w-5" />
              <span className="font-semibold text-sm">Ending Soon</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-heading">
              Don't Miss These Auctions
            </h2>
            <p className="text-slate-600 mt-2 text-sm sm:text-base">
              Properties going under the hammer in the next 72 hours
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3].map(i => (
                <div key={i} className="bg-slate-100 rounded-2xl h-80 animate-pulse" />
              ))}
            </div>
          ) : endingSoon.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {endingSoon.map((property) => (
                  <PropertyCard key={property.property_id} property={property} />
                ))}
              </div>
              <div className="text-center mt-8">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/properties?ending=soon')}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                >
                  View All Ending Soon <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </>
          ) : (
            <p className="text-center text-slate-500">No auctions ending in the next 72 hours.</p>
          )}
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-14 sm:py-20 bg-slate-50" data-testid="categories-section">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4 font-heading">
              Browse by Category
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto text-sm sm:text-base px-2">
              Explore auction properties across residential, commercial, and land categories
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {categories.map((category) => (
              <div
                key={category.name}
                className="group cursor-pointer bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100"
                onClick={() => navigate(`/properties?category=${category.name}`)}
                data-testid={`category-card-${category.name.toLowerCase()}`}
              >
                <div className="relative h-40 sm:h-48 overflow-hidden">
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
                  <div className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-[#F5A623] text-white text-xs font-bold px-2.5 py-1 rounded-full">
                    {category.count}
                  </div>
                </div>
                <div className="p-4 sm:p-6 text-center">
                  <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2">
                    <div className="p-1.5 sm:p-2 bg-[#F5A623]/10 rounded-lg">
                      <category.icon className="h-4 w-4 sm:h-5 sm:w-5 text-[#F5A623]" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900">{category.name}</h3>
                  </div>
                  <p className="text-slate-500 text-xs sm:text-sm mb-3 sm:mb-4">{category.description}</p>
                  <div className="flex items-center justify-center text-[#F5A623] font-medium text-xs sm:text-sm group-hover:gap-2 transition-all">
                    Browse {category.name} <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 ml-1" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Auction Houses Trust Section - NEW */}
      <section className="py-10 sm:py-14 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 rounded-full px-4 py-2 mb-4">
              <Shield className="h-5 w-5" />
              <span className="font-semibold text-sm">Trusted Sources</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-heading">
              Aggregating from 40+ UK Auction Houses
            </h2>
            <p className="text-slate-600 mt-2 text-sm sm:text-base max-w-2xl mx-auto">
              We collect listings from major UK property auctioneers and display them in one place
            </p>
          </div>
          
          <div className="bg-slate-50 rounded-2xl p-6 sm:p-8">
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
              {auctionHouses.slice(0, 20).map((house, idx) => (
                <span 
                  key={idx} 
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white border border-slate-200 rounded-lg text-xs sm:text-sm text-slate-700 font-medium hover:border-[#F5A623] hover:text-[#F5A623] transition-colors cursor-default"
                >
                  {house.name}
                </span>
              ))}
              {auctionHouses.length > 20 && (
                <span className="px-4 py-2 bg-[#F5A623]/10 border border-[#F5A623]/30 rounded-lg text-sm text-[#F5A623] font-medium">
                  +{auctionHouses.length - 20} more
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Finance Services Banner */}
      <section className="py-10 sm:py-14 md:py-16 bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex flex-col items-center text-center gap-6 sm:gap-8">
            <div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 font-heading">
                Need Finance for Your Auction Purchase?
              </h2>
              <p className="text-slate-400 text-sm sm:text-base">
                We offer competitive rates on bridging loans, mortgages, and development finance
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
              {financeServices.map((service) => (
                <Link
                  key={service.name}
                  to={service.href}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-slate-800 hover:bg-[#F5A623] text-white text-xs sm:text-sm font-medium rounded-lg transition-colors"
                >
                  {service.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-14 sm:py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4 font-heading">
              Why Choose AuctionGuru?
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto text-sm sm:text-base px-2">
              Everything you need to find and win your perfect auction property
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="group p-6 md:p-8 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-100 hover:border-[#F5A623]/30 hover:shadow-lg transition-all flex flex-col items-center text-center" data-testid="feature-realtime">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-[#F5A623]/10 rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#F5A623]/20 transition-colors flex-shrink-0">
                <TrendingUp className="h-6 w-6 md:h-7 md:w-7 text-[#F5A623]" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-2">Real-Time Updates</h3>
              <p className="text-slate-600 text-sm md:text-base leading-relaxed flex-grow">
                Get instant access to new listings from 40+ UK auction houses, updated daily at 6am GMT.
              </p>
            </div>

            <div className="group p-6 md:p-8 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-100 hover:border-[#F5A623]/30 hover:shadow-lg transition-all flex flex-col items-center text-center" data-testid="feature-alerts">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-[#F5A623]/10 rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#F5A623]/20 transition-colors flex-shrink-0">
                <Bell className="h-6 w-6 md:h-7 md:w-7 text-[#F5A623]" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-2">Smart Alerts</h3>
              <p className="text-slate-600 text-sm md:text-base leading-relaxed flex-grow">
                Never miss an opportunity. Set up custom alerts and get notified when properties match your criteria.
              </p>
            </div>

            <div className="group p-6 md:p-8 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-100 hover:border-[#F5A623]/30 hover:shadow-lg transition-all flex flex-col items-center text-center sm:col-span-2 lg:col-span-1" data-testid="feature-calendar">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-[#F5A623]/10 rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#F5A623]/20 transition-colors flex-shrink-0">
                <Calendar className="h-6 w-6 md:h-7 md:w-7 text-[#F5A623]" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-2">Auction Calendar</h3>
              <p className="text-slate-600 text-sm md:text-base leading-relaxed flex-grow">
                Plan ahead with our comprehensive auction calendar showing all upcoming auctions across the UK.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-[#F5A623] to-[#E59512]">
        <div className="container mx-auto px-6 md:px-12 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 font-heading">
            Ready to Find Your Next Property?
          </h2>
          <p className="text-white/90 mb-6 sm:mb-8 max-w-xl mx-auto text-sm sm:text-base px-2">
            Join thousands of property investors who trust AuctionGuru for their auction property search
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4">
            <Button 
              size="lg" 
              onClick={() => navigate('/properties')}
              className="bg-white text-[#F5A623] hover:bg-slate-100 font-semibold px-6 sm:px-8 rounded-xl text-sm sm:text-base"
            >
              Browse Properties
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => navigate('/calendar')}
              className="border-white text-white hover:bg-white hover:text-[#F5A623] font-semibold px-6 sm:px-8 rounded-xl text-sm sm:text-base"
            >
              View Auction Calendar
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
